import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { RentalApplicationBillingSettingsRepository } from '~/repositories/settings/rentalApplicationBillingSettingsRepository';
import type { Accessor } from 'solid-js';
import type { RentalApplicationBillingSettings } from '~/swagger/settings/Api';

interface RentalApplicationPaymentAccountContextValue {
  isLoading: Accessor<boolean>;
  error: Accessor<Error | undefined>;
  updateRentalApplicationPaymentAccount: (
    payload: RentalApplicationBillingSettings
  ) => Promise<RentalApplicationBillingSettings | undefined>;
  getRentalApplicationPaymentAccount: () => Promise<RentalApplicationBillingSettings | undefined>;
  deleteRentalApplicationPaymentAccount: () => Promise<void>;
  rentalApplicationPaymentAccount: Accessor<RentalApplicationBillingSettings | undefined>;
}

export const [RentalApplicationPaymentAccountProvider, useRentalApplicationPaymentAccount] =
  createMagicDoorContext<RentalApplicationPaymentAccountContextValue>('RentalApplicationPaymentAccount', () => {
    const repo = new RentalApplicationBillingSettingsRepository();
    const [rentalApplicationPaymentAccount, setRentalApplicationPaymentAccount] = createSignal<
      RentalApplicationBillingSettings | undefined
    >(undefined);
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);

    const getRentalApplicationPaymentAccount = async (): Promise<RentalApplicationBillingSettings | undefined> => {
      setIsLoading(true);

      try {
        const result = await repo.getRentalApplicationBillingSettings();
        setRentalApplicationPaymentAccount(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const updateRentalApplicationPaymentAccount = async (
      payload: RentalApplicationBillingSettings
    ): Promise<RentalApplicationBillingSettings | undefined> => {
      setIsLoading(true);
      setError(undefined);
      try {
        const result = await repo.updateRentalApplicationBillingSettings(payload);
        setRentalApplicationPaymentAccount(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const deleteRentalApplicationPaymentAccount = async (): Promise<void> => {
      setIsLoading(true);

      try {
        await repo.deleteRentalApplicationBillingSettings();
        setRentalApplicationPaymentAccount(undefined);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    return {
      isLoading,
      error,
      updateRentalApplicationPaymentAccount,
      getRentalApplicationPaymentAccount,
      deleteRentalApplicationPaymentAccount,
      rentalApplicationPaymentAccount,
    };
  });
